import moment from 'moment';
import { Price_Resp } from '../../store/calcCache/definitions';
import {
  GraphData,
  GraphDataBundle,
  GraphPoint,
  SegmentGraphStat,
} from './defs';
import { Event } from 'store/event/definitions';
import { CompareDataSet } from 'store/compareCache/definitions';

const possibleSegments = ['driveup', 'booking', 'total'] as const;

const dummyZeroCompareEntry = { revenue: 0 };

let emptyNum = [] as number[];

const dummyStartInFuture = moment.utc(
  `${new Date().getFullYear() + 100}-01-01`
);

const getStartTimeFor = (zones: Price_Resp['zones'], compare: boolean) =>
  zones.reduce((accZones: moment.Moment, cur) => {
    let seriesStartTime = cur.series.reduce((accSeries: moment.Moment, cur) => {
      if ((cur.clientIsForCompare ?? false) !== compare) return accSeries;
      let curSeriesTime = moment.utc(cur.startTime);

      return curSeriesTime.isSameOrBefore(accSeries)
        ? curSeriesTime
        : accSeries;
    }, dummyStartInFuture) as moment.Moment;

    return seriesStartTime.isSameOrBefore(accZones)
      ? seriesStartTime
      : accZones;
  }, dummyStartInFuture);

export const dataZip = (
  zones: Price_Resp['zones'],
  segment: 'all' | 'driveup' | 'booking',
  //priceType: 'hour' | 'day' | 'week' | 'hour_avg',
  selectedPriceViewTypeId: string,
  accumulatedHourCount: number,
  firstStep: number,
  zoneIds: number[],
  comparedTo?: CompareDataSet | null,
  // Sentinel -1000 means "no VAT rate configured" -> pass revenue through unscaled.
  // Source: site?.config?.grossIncludedTaxRatePercentage (see selectCurrentFacility).
  vatRate: number = -1000,
  // Selected period end ('YYYY-MM-DD'), used to clamp the tick range below.
  selectedEndDate?: string
) => {
  //console.log('SelectedPriceViewTID:', selectedPriceViewTypeId);

  const usedSegments = possibleSegments.filter(
    (ps) => segment === 'all' || segment === ps
  );

  // The input zones are dynamic zones separated by segment (ie more like a list of zone-segment informations)
  // Filter out non-selected zones and segments. (unless we have all, either byh segment=="all" or zoneId===undefined)
  const actZones = (zones || [])
    .filter((zn) => {
      if (zoneIds.length > 0) {
        if (!zoneIds.find((z) => z === Number(zn.zoneId))) {
          return false;
        }
      }
      if (segment === 'all') return true;
      return segment === zn.kind;
    })
    .map((zn) => ({ ...zn, segmentIndex: usedSegments.indexOf(zn.kind) }));

  // create zone accumulators
  const zoneAccs = actZones.map((zn) => ({
    demAcc: 0,
    odemAcc: 0,
    //    scale: zn.year.estiatedCustomerGrowthPercentage / 100 + 1,
  }));

  const zoneComparesData = !comparedTo
    ? []
    : actZones.map((az) => comparedTo?.dataByZone[az.zoneId]);

  //const realZoneCount = new Set(zones.map((z) => z.zoneid)).size;
  const realZoneCount = 1;

  // Take the max count from all zones (some zones might have less data for various reasons)
  // Compare series are deliberately excluded: they cover a *different* date span
  // (the compare period) and the graph's x-axis must stay inside the selected
  // period. Counting them here used to stretch the tick range past the selected
  // end date, and the main series then read out-of-bounds and rendered as a long
  // tail of zeroes. Compare series longer than the selected period are truncated,
  // shorter ones simply stop.
  let hourCount = actZones.reduce(
    (acc, cur) =>
      Math.max(
        acc,
        cur.series.reduce(
          (acc, series) =>
            (series.clientIsForCompare ?? false)
              ? acc
              : Math.max(
                  acc,
                  (series.values.length * series.stepInSeconds) / (60 * 60)
                ),
          0
        )
      ),
    0
  );
  // const count = Math.max(
  //   bookings
  //     .map((booking) => booking.hours.length)
  //     .reduce((a, b) => Math.max(a, b), 0),
  //   driveups.map((du) => du.hours.length).reduce((a, b) => Math.max(a, b), 0)
  // );

  const soAccRev = usedSegments.map((s) => 0);
  const snAccRev = usedSegments.map((s) => 0);
  const sdAccRev = usedSegments.map((s) => 0);
  // let boAccRev = 0;
  // let doAccRev = 0;
  let toAccRev = 0;
  // let bnAccRev = 0;
  // let dnAccRev = 0;
  let tnAccRev = 0;
  let tdAccRev = 0;

  let comAccRev = 0;

  type ZoneInfo = Price_Resp['zones'][number];
  type HourEntry = ZoneInfo['hours'][number];

  let demandAcc = 0;
  let oldDemandAcc = 0;

  const data = [] as GraphPoint[];

  let hasBooking = false;
  let hasDriveup = false;

  let span = firstStep;

  let compSkip = 0;
  if (comparedTo && actZones.length > 0 && actZones[0].hours.length > 0) {
    compSkip = moment
      .utc(actZones[0].hours[0].time)
      .diff(moment.utc(comparedTo.viewStartDate), 'hours');
  }

  // Make a unique list of series [outID] , zones can have different order and occurences
  //  of series so we build a mapping of existing ones and only accumlate those that exist per-zone to the full
  const seriesIDs = [
    ...actZones
      .flatMap((az) =>
        az.series
          .filter((s) => s.stepInSeconds / 60 / 60 <= accumulatedHourCount)
          .map((s) => s.outID)
      )
      // this last part makes the list unique
      .reduce((a, c) => (a.add(c), a), new Set<string>()),
  ];

  const sourceSeries = seriesIDs.map((sid) =>
    actZones.flatMap((az) => az.series.filter((ser) => ser.outID === sid))
  );

  // Net-revenue conversion: revenue series (identified by valueKind, not by OutID
  // string-matching) are divided by (1 + vatRate/100) exactly once, here at the
  // source. Everything downstream (bars, axis, acc_revenue_* series, tdAccRev)
  // then derives from already-net values. Price/arrivals/occupancy series are
  // untouched. When vatRate is the "unconfigured" sentinel (-1000) no scaling
  // happens at all.
  const revenueDivisor = vatRate > -1000 ? (100 + vatRate) / 100 : 1;
  const seriesIsRevenue = seriesIDs.map((sid, idx) => {
    const src = sourceSeries[idx];
    return !!src && src.length > 0 && src[0].valueKind === 'revenue';
  });

  const npriceIndex = seriesIDs.findIndex(
    (sid) => sid === selectedPriceViewTypeId
  );
  const revenueIndex = seriesIDs.findIndex((sid) => sid === 'revenue');
  const demandIndex = seriesIDs.findIndex((sid) => sid === 'arrivals');
  const hdemandIndex = seriesIDs.findIndex((sid) => sid === 'actualarrivals');

  var duRevIdx = seriesIDs.findIndex((s) => s === 'revenue_driveup');
  var boRevIdex = seriesIDs.findIndex((s) => s === 'revenue_booking');
  var cduRevIdx = seriesIDs.findIndex(
    (s) => s === '__compare__revenue_driveup'
  );
  var cboRevIdex = seriesIDs.findIndex(
    (s) => s === '__compare__revenue_booking'
  );

  seriesIDs.push('tdAccRev');
  seriesIDs.push('comAccRev');
  // tdAccRev/comAccRev are derived (below) from already-scaled revenue_* series,
  // so they must not be scaled again here.
  seriesIsRevenue.push(false, false);

  let prevOn: GraphPoint | null = null;

  let startOfCurrentDay = moment(new Date()).startOf('day');
  let startTime = getStartTimeFor(actZones, false);

  // The x-axis must never run past the selected period. hourCount above is just
  // the longest series handed to us, so a series the backend extends beyond the
  // requested end date -- a prediction running to "now", say -- used to stretch
  // the tick range with it. The main series then read out of bounds and rendered
  // as a tail of zeroes with the accumulators flatlined. Compare series are
  // already excluded from hourCount; this clamps every other cause.
  if (selectedEndDate) {
    const selectedHours = moment
      .utc(selectedEndDate)
      .add(1, 'day')
      .diff(startTime, 'hours');
    if (selectedHours > 0) hourCount = Math.min(hourCount, selectedHours);
  }
  let compStartTime = getStartTimeFor(actZones, true);

  let compHourOffset = startTime.diff(compStartTime, 'hours');

  let seriesHourOffsets = actZones.map((az, azIdx) => {
    return az.series.map((ser, serIdx) => {
      return {
        offset: moment.utc(ser.startTime).diff(startTime, 'hours', true),
      };
    });
  });

  // BIG NESTED-LOOP AHEAD, sane but needs an intro:
  // - The purpose is to accumulate values for each graph from a series of datapoints that might NOT be in the same time resolution.
  // The loop iterations are as follows:
  // i : main iterations counted in hours done for the number of output ticks, stepping over hours if a tick is more than one hour.
  // j : steps through each hour between the main "i" ticks.
  // k : for each zone (ie we sum from all input zones for each tick)
  // l : for each series, looking up inside the series by offsetting from the main-i/j-loop hour
  // m : auxillary

  // go through data in acc increments
  for (let i = 0; i < hourCount; i += span) {
    const timeValue = startTime.clone().add(i, 'hours');
    //const timeValue = actZones[0].hours[i].time; // (bookings?.hours[i].time || driveups?.hours[i].time)!;
    const outerDate = timeValue.toDate();
    const currentEntryStepSize = span;

    // Only use first step on the first iteration, used to handle not compmlete first week of the year
    if (i >= firstStep) {
      span = accumulatedHourCount;
    }

    const mk0Revenue = () => ({ current: 0, accumulated: 0 });

    const on: GraphPoint = {
      tick: (span >= 24 ? timeValue.clone().startOf('day') : timeValue) //moment(span >= 24 ? timeValue.substring(0, 10) : timeValue)
        //.utc()
        .toISOString(),
      odate: timeValue.toString(),
      date:
        span >= 24
          ? timeValue.toISOString().substring(0, 10)
          : timeValue.toISOString(), // timeValue.substring(0, 10) : timeValue,

      demand: 0,

      segments: usedSegments.map((s) => ({
        demand: 0,
        newRevenue: mk0Revenue(),
        oldRevenue: mk0Revenue(),
        defRevenue: mk0Revenue(),
      })),

      seriesID: seriesIDs,
      series: seriesIDs.map((s) => 0),
      // Accumulators carry forward from the previous tick. A tick where a series
      // has no sample -- a null hole, or an index past the end of a series that
      // is shorter than the tick range -- hits a `continue` below that skips the
      // `seriesAcc[l] = prev + series[l]` assignment. Seeding with 0 made those
      // ticks reset the running total to zero, and every later tick then
      // accumulated from that zero, so the accumulated revenue line (and the Net
      // Revenue sticker, which reads the last point's tdAccRev) reported only the
      // revenue after the last gap -- always lower than the true total. Seeding
      // from prevOn leaves a gap tick holding the total accumulated so far.
      seriesAcc: seriesIDs.map((s, sidx) => prevOn?.seriesAcc[sidx] ?? 0),

      // // demand

      // old revenue
      toRevenue: 0,
      toAccRev: 0,
      // new revenue
      tnRevenue: 0,
      tnAccRev: 0,
      // default revenue
      tdRevenue: 0,
      tdAccRev: 0,

      // compare revenue
      comRevenue: 0,
      comAccRev: 0,

      price: 0,
      isDemandPrediction: false,

      dateMonth: outerDate.getMonth(),
      dateDay: outerDate.getDay(),
    };

    let spanCount = Math.min(currentEntryStepSize, hourCount - i); // we need the span-count to adjust for end-of-year weeks that don't span an entire week.
    // go through all hours within this span
    for (let j = 0; j < spanCount; j++) {
      // and then for each zone.
      for (let k = 0; k < actZones.length; k++) {
        const zone = actZones[k];

        const zoneAcc = zoneAccs[k];

        // don't try to accumulate out-of-span hours.
        if (i + j >= hourCount) continue;
        // var seriesFinalAcc: {
        //   electric: {
        //     [key: string]: number;
        //   };
        // } = {
        //   electric: {},
        // };
        for (let l = 0; l < seriesIDs.length; l++) {
          if (seriesIDs[l] === 'tdAccRev' || seriesIDs[l] === 'comAccRev') {
            on.series[l] =
              seriesIDs[l] === 'tdAccRev' ? on.tdAccRev : on.comAccRev;

            on.seriesAcc[l] =
              seriesIDs[l] === 'tdAccRev' ? on.tdAccRev : on.comAccRev;
            continue;
          }
          let seriesData: (typeof zone.series)[0] = null as any;
          let offsetInfo: (typeof seriesHourOffsets)[0][0] = null as any;
          // Find if this datapoint has something matching the "global" seriesID (we could for example have non-electric and electric zones.. only electric zones would have electric surcharges)
          for (let m = 0; m < zone.series.length; m++) {
            if (zone.series[m].outID !== seriesIDs[l]) continue;
            seriesData = zone.series[m];
            offsetInfo = seriesHourOffsets[k][m];
          }
          if (!seriesData) {
            continue;
          }

          // Only sample as many samples as we have available.
          let seriesStepInHours = seriesData.stepInSeconds / 60 / 60;
          if (0 !== (i + j) % seriesStepInHours) {
            continue;
          }

          // The entry in the series we will currently index depends on the step-size
          let seriesIdx =
            (i +
              j -
              offsetInfo.offset - // offset by our starting position
              ((seriesData.clientIsForCompare ?? false) ? compHourOffset : 0)) / // adjust by the start difference if the series is a compare series
            seriesStepInHours;

          // Bounds first: a series that covers a shorter or shifted span than the
          // tick range has no value here at all, and reading it out-of-bounds
          // yields undefined (not null), which would slip past the hole-check below.
          if (seriesIdx < 0 || seriesIdx >= seriesData.values.length) continue; // don't read out-of-bounds

          if (seriesData.values[seriesIdx] === null) {
            on.series[l] = null;
            continue;
          }

          // Scale to net exactly once at the source, for revenue series only.
          const rawValue = seriesData.values[seriesIdx];
          const seriesValue = seriesIsRevenue[l]
            ? rawValue / revenueDivisor
            : rawValue;

          switch (seriesData.compacting) {
            case 'ACCUMULATE':
              {
                on.series[l]! += seriesValue;
                on.seriesAcc[l] = (prevOn?.seriesAcc[l] ?? 0) + on.series[l]!;
                // seriesFinalAcc[
                //   seriesData.category as keyof typeof seriesFinalAcc
                // ][seriesData.valueKind] = on.seriesAcc[l];
              }
              break;
            case 'AVERAGE':
              {
                on.series[l]! += seriesValue / (spanCount / seriesStepInHours);
                on.seriesAcc[l] = (prevOn?.seriesAcc[l] ?? 0) + on.series[l]!;
              }
              break;
            case 'SAMPLEHEAD':
              {
                on.series[l] = seriesValue;
                on.seriesAcc[l] = on.series[l]!;
              }
              break;
            default:
              break;
          }
        }

        // // if any hour within the output span is an hour prediction we mark all of them
        const curIsPred = timeValue.isSameOrAfter(startOfCurrentDay);
        on.isDemandPrediction = on.isDemandPrediction || curIsPred; // || hourEntry.isDemandPrediction;

        on.price = on.series[npriceIndex]!;
        // When a single segment is selected the other segment's series is absent,
        // so its findIndex above returned -1 and seriesAcc[-1] is undefined.
        // Coalescing to 0 keeps these accumulators numeric — without it the sum
        // becomes NaN and the sticker's growth percentage renders as "NaN%".
        if (!!zone.series[0].clientIsForCompare) {
          on.comAccRev = Number(
            (
              (on.seriesAcc[cduRevIdx] ?? 0) + (on.seriesAcc[cboRevIdex] ?? 0)
            ).toFixed(0)
          );
        } else {
          on.tdAccRev = Number(
            (
              (on.seriesAcc[duRevIdx] ?? 0) + (on.seriesAcc[boRevIdex] ?? 0)
            ).toFixed(0)
          );
        }

        if (zone.kind === 'booking') {
          hasBooking = true;
        } else if (zone.kind === 'driveup') {
          hasDriveup = true;
        }

        // const arrivalCount = demandIndex != -1 ? on.series[demandIndex] : 0;
        // on.segments[zone.segmentIndex].demand += arrivalCount; // hourEntry.parkings;
        // on.demand += arrivalCount;

        // demandAcc += arrivalCount;

        // zoneAcc.demAcc += arrivalCount;

        // const harrivalCount = hdemandIndex != -1 ? on.series[hdemandIndex] : 0;
        // oldDemandAcc += harrivalCount;
        // zoneAcc.odemAcc += harrivalCount;

        // on.segments[zone.segmentIndex].newRevenue.current +=
        //   hourEntry.newRevenue;
        // on.tnRevenue += hourEntry.newRevenue;
        // snAccRev[zone.segmentIndex] += hourEntry.newRevenue;
        // tnAccRev += hourEntry.newRevenue;
        // on.segments[zone.segmentIndex].newRevenue.accumulated = Math.floor(
        //   snAccRev[zone.segmentIndex]
        // );
        // on.tnAccRev = Math.floor(tnAccRev);

        // on.segments[zone.segmentIndex].oldRevenue.current +=
        //   hourEntry.oldRevenue;
        // on.toRevenue += hourEntry.oldRevenue;
        // soAccRev[zone.segmentIndex] += hourEntry.oldRevenue;
        // toAccRev += hourEntry.oldRevenue;
        // on.segments[zone.segmentIndex].oldRevenue.accumulated = Math.floor(
        //   soAccRev[zone.segmentIndex]
        // );
        // on.toAccRev = Math.floor(toAccRev);

        // const defRevenue = hourEntry.isDemandPrediction
        //   ? hourEntry.newRevenue
        //   : hourEntry.oldRevenue;

        // on.segments[zone.segmentIndex].defRevenue.current += defRevenue;
        // on.tdRevenue += defRevenue;
        // sdAccRev[zone.segmentIndex] += defRevenue;
        // tdAccRev += defRevenue;
        // on.segments[zone.segmentIndex].defRevenue.accumulated = Math.floor(
        //   sdAccRev[zone.segmentIndex]
        // );
        // //+ Math.floor(seriesEvTotalRevenue);
        // on.tdAccRev = Math.floor(tdAccRev); // + Math.floor(seriesEvTotalRevenue);

        // on.comRevenue += compEntry.revenue;
        // comAccRev += compEntry.revenue;
        // on.comAccRev = Math.floor(comAccRev);
      }
    }
    on.demand = Math.floor(on.demand);
    prevOn = on;
    data.push(on);
  }

  let oldDiv = zoneAccs.reduce((prev, za) => prev + za.odemAcc, 0);
  if (oldDiv < 0.0000000001) oldDiv = 1;
  const custAccDiff =
    zoneAccs.reduce((prev, za) => prev + za.demAcc, 0) / oldDiv;

  const ov = {
    data,
    bookings: hasBooking,
    driveups: hasDriveup,
    realZoneCount,
    demandAcc,
    oldDemandAcc,
    custAccDiff,
    segments: usedSegments,
    seriesIDs: seriesIDs,
  } as GraphData;

  return ov;
};

// Recharts fills a fixed [0, max] domain by walking a round step up from zero and
// then pinning max as the last tick, dropping whichever tick would collide with it.
// A [0, 15.5] domain therefore draws 0, 4, 8, 15.5 -- the 12 is gone. Rounding max
// up to a whole number of round steps keeps every tick on the ladder.
// Every axis this feeds counts cars or currency, so the ticks are kept whole:
// numToSpacedString floors, and a 2.5 step would label 2.5 and 5 as "2" and "5".
export const findAxisMax = (v: number, tickCount = 5) => {
  const intervals = Math.max(1, tickCount - 1);
  // Also the no-data case: [0, 0] is not a domain Recharts can scale.
  if (!isFinite(v) || v <= intervals) return intervals;

  const roughStep = v / intervals;
  const exp = Math.pow(10, Math.floor(Math.log10(roughStep)));

  // roughStep < 10 * exp by construction, so the last mantissa always matches.
  for (const mantissa of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    const step = mantissa * exp;
    if (step < roughStep - 1e-9) continue;
    if (!Number.isInteger(step)) continue;
    return Number((step * intervals).toPrecision(12));
  }
  return v;
};

// The ticks for an axis whose max came from findAxisMax. Handing Recharts the list
// rather than letting it derive one is what guarantees the evenly spaced ladder.
export const axisTicks = (max: number, tickCount = 5) => {
  const intervals = Math.max(1, tickCount - 1);
  return Array.from({ length: intervals + 1 }, (_, i) =>
    Number(((max * i) / intervals).toPrecision(12))
  );
};

export const findGraphLevel = (v: number) => {
  for (let i = 0; i < 2000000; i++) {
    const step = 3;
    const maxDig = [1, 2, 5][i % step];
    const maxExp = Math.pow(10, Math.floor(i / step));
    const tMax = maxExp * maxDig;
    if (v < tMax) return tMax;
  }
  return 1;
};

// this takes the highest digit, increases by one
export const roundUpMax = (v: number) => {
  // start by making a ceiled str of the number
  let str = '' + Math.ceil(v);
  // if it starts with a 9 (like in 90 smth, or 900 smth,etc, we append a 0 so that we've bumped the number of digits)
  if (str.startsWith('9')) str = '0' + str;
  // now split and rebuild the string so that everything but the first digit is cleared and the first one bumped.
  return parseInt(
    str
      .split('')
      .map((v, i) => {
        return i === 0 ? parseInt(v) + 1 + '' : '0';
      })
      .join('')
  );
};

export const firstToUpper = (v: string) => {
  if (v.length < 1) return v;
  return v.substring(0, 1).toUpperCase() + v.substring(1);
};

export function formatNumberWithSpaces(number: number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export const numToSpacedString = (v: number) => {
  let out = '';
  if (v >= 1000000) {
    return Math.floor(v / 100000) / 10 + ' M';
  }
  if (v >= 1000) {
    return Math.floor(v / 100) / 10 + ' K';
  }
  return Math.floor(v) + out;
};

export const isSpecialDay = (date: Date, data: GraphDataBundle) => {
  if (!data.events) return { eventExists: false, event: null };
  //console.log('Facility-check');
  var event = data.events.reduce((acc, ev) => {
    let sd = moment.utc(ev.startDate).unix(); //  new Date(ev.startDate);
    let ed = moment.utc(ev.endDate).unix(); //new Date(ev.endDate);
    let odate = moment.utc(date).unix(); //new Date(date);
    // zoneId , routeId in ev
    // if data.zone , then data.zone.id , data.facility needs id
    if (sd <= odate && odate <= ed) {
      if (
        // for viewing a zone there are 2 disrtinct cases
        (data.zones.length > 0 &&
          // Either it's a facility-wide event, in that case it's enough to be in the correct one.
          ((ev.routeData && ev.routeID === data.facility.id) ||
            // .. or if it's a event for specific zones then that zone must be included!
            (ev.zoneData &&
              ev.zoneData.some(
                (zd) => !!data.zones.find((z) => z.id === zd.zoneID)
              )))) ||
        // If we're viewing all zones and the facility ID matches then we're showing this event
        (data.zones.length === 0 && ev.routeID === data.facility.id)
      ) {
        acc.push(ev);
      }
    }
    return acc;
  }, [] as Event[]);
  if (event.length > 0) return { eventExists: true, event };
  return { eventExists: false, event: null };
};

